import type { UpdateThirdPartyResponseResponse } from '@risksmart-app/trpc/src/types/third-party-response.types';
import { TppUpdateThirdPartyResponseDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '../utils/hook-factories';
import type { MutationCompatibilityResult } from './transportCompatibility';
import type { UpdateResponseArgs } from './useTppUpdateResponseTypes';

const mapResponse = (result: UpdateThirdPartyResponseResponse) => ({
  __typename: 'mutation_root' as const,
  update_third_party_response_by_pk: {
    __typename: 'third_party_response' as const,
    Id: result.Id,
  },
});

export type TppUpdateResponseResult = MutationCompatibilityResult<
  ReturnType<typeof mapResponse>
>;

/**
 * Flag-gated save of questionnaire progress (RSP-6570). Built on the portal's
 * `createMutationHook`: flag off → the legacy Apollo `tppUpdateThirdPartyResponse`
 * mutation; flag on → the tRPC `portal.thirdPartyResponses.update` mutation
 * (async request flow / ObjectEventStrategy). `status` is narrowed to the two
 * respondent-writable states; the data-layer processor additionally guards the
 * current status is still editable (409) and that the caller is the invitee
 * (403). The domain union's values equal the GraphQL enum members, so the same
 * value feeds both the tRPC input and the legacy Apollo variable.
 */
const useUpdateResponseHook = createMutationHook<
  UpdateResponseArgs,
  UpdateResponseArgs,
  UpdateThirdPartyResponseResponse,
  TppUpdateResponseResult,
  'updateResponse'
>({
  name: 'updateResponse',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.portal.thirdPartyResponses.update.mutationOptions({
      onSuccess,
    }),
  mapInput: (input) => input,
  // Invalidate the respondent's list + detail feeds explicitly rather than
  // relying on `onSave` navigating away. Freshness was previously an implicit
  // consequence of `navigate('/')` remounting the home page under
  // `staleTime: 0` / `refetchOnMount: true` — correct, but it silently breaks
  // the moment the save flow stops navigating (e.g. an inline "Saved" toast),
  // leaving a submitted questionnaire showing its old status for up to 30s.
  invalidate: (trpc) => [
    trpc.frontend.portal.thirdPartyResponses.list.queryKey(),
    trpc.frontend.portal.thirdPartyResponses.getById.queryKey(),
  ],
  mapResponse,
  graphqlDocument: TppUpdateThirdPartyResponseDocument,
  graphqlVariables: ({ id, responseData, status }) => ({
    Id: id,
    response: responseData,
    status,
  }),
});

/**
 * Returns the bare save callable so the questionnaire page keeps its
 * `const update = useTppUpdateResponse(); await update({ ... })` call site.
 */
export const useTppUpdateResponse = () =>
  useUpdateResponseHook().updateResponse;
